import React from "react";
import { Link } from "react-router-dom";
import { Box, Stack } from "@mui/material";
import type { GridColDef, GridSortModel } from "@mui/x-data-grid";
import { showTitleWhenClipped } from "utils/showTitleWhenClipped";
import type { BillingAccountDto } from "client/api-v2";
import { DataTable } from "components/DataTable";
import { EmptyValue } from "components/MutedPlaceholder";
import { billingAccountRoutes } from "../billing-account-routes";
import { AccountIssueChip } from "./AccountIssueChip";
import { ResellerChip } from "./ResellerChip";

interface BillingAccountListProps {
  billingAccounts: BillingAccountDto[];
  totalCount: number;
  loading?: boolean;
  page: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
}

// A cell clips its overflow silently, so the badges keep their width and a long name gives way to
// an ellipsis — otherwise the account with most to flag would show none of it.
const badgeStyles = { flexShrink: 0 };

const nameStyles = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// The common name isn't shown: search matches it server-side, and the detail page carries it.
function AccountName({ account }: { account: BillingAccountDto }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0, width: "100%" }}>
      <Box sx={nameStyles} onMouseEnter={showTitleWhenClipped(account.legalName)}>
        <Link to={billingAccountRoutes.detail(account.code).root}>{account.legalName}</Link>
      </Box>
      {account.isReseller && <ResellerChip sx={badgeStyles} />}
      {account.hasAddressIssue && <AccountIssueChip issue="address" sx={badgeStyles} />}
      {account.hasContactsIssue && <AccountIssueChip issue="contacts" sx={badgeStyles} />}
    </Stack>
  );
}

const columns: GridColDef<BillingAccountDto>[] = [
  {
    field: "legalName",
    headerName: "Name",
    minWidth: 360, // Clears the three badges, which don't shrink.
    flex: 1,
    renderCell: (params) => <AccountName account={params.row} />,
  },
  {
    field: "crmCustomerId",
    headerName: "CRM Account",
    minWidth: 140,
    renderCell: (params) => params.row.crmCustomerId ?? <EmptyValue />,
  },
  {
    field: "zuoraAccountNumber",
    headerName: "Zuora Account",
    minWidth: 140,
    renderCell: (params) => params.row.zuoraAccountNumber ?? <EmptyValue />,
  },
  {
    // ISO code until the admin portal can reach the country list the backend validates against (GPT-618).
    // The country lives on the address rather than on the row, so the value has to be got explicitly:
    // without this the cell reads as populated while the grid holds nothing behind it.
    field: "countryCode",
    headerName: "Country",
    minWidth: 100,
    valueGetter: (_, row) => row.address?.countryCode,
    renderCell: (params) => params.row.address?.countryCode ?? <EmptyValue />,
  },
  {
    field: "isTaxExempt",
    headerName: "Tax Exempt",
    minWidth: 110,
    sortable: false,
    renderCell: (params) => (params.row.isTaxExempt ? "Yes" : <EmptyValue />),
  },
  {
    field: "contactCount",
    headerName: "Contacts",
    minWidth: 100,
  },
];

export function BillingAccountList({
  billingAccounts,
  totalCount,
  loading,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sortModel,
  onSortModelChange,
}: BillingAccountListProps) {
  return (
    <DataTable
      rows={billingAccounts}
      columns={columns}
      getRowId={(account) => account.code}
      rowCount={totalCount}
      loading={loading}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      pagingMode="server"
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
}
